import type { ImageTheme } from './Image';

/**
 * Centralized image size configurations for all components.
 * theme values are keys resolved by Image.tsx's own theme map.
 */
export const imageSizes = {
  card: {
    width: 432,
    height: 243,
    isFluid: true,
    theme: ['mosaic', 'rounded'] as ImageTheme[],
    sources: [
      { media: '(min-width: 1600px)', width: 432, height: 243 },
      { media: '(min-width: 1280px)', width: 379, height: 213 },
      { media: '(min-width: 1024px)', width: 452, height: 254 },
      { media: '(min-width: 768px)', width: 340, height: 191 },
      { media: '(min-width: 510px)', width: 462, height: 260 },
      { media: '(max-width: 509px)', width: 330, height: 186 }
    ],
    sizes:
      '(min-width: 1600px) 432px, (min-width: 1280px) 379px, (min-width: 1024px) 452px, (min-width: 768px) 340px, (min-width: 510px) 462px, 330px'
  },

  pageHeader: {
    width: 800,
    height: 400,
    isFluid: true,
    theme: 'anchored' as ImageTheme,
    sources: [
      { media: '(min-width: 1600px)', width: 800, height: 400 },
      { media: '(min-width: 1280px)', width: 640, height: 436 },
      { media: '(min-width: 1024px)', width: 512, height: 474 },
      { media: '(min-width: 768px)', width: 752, height: 235 },
      { media: '(min-width: 510px)', width: 494, height: 235 },
      { media: '(max-width: 509px)', width: 314, height: 235 }
    ],
    sizes: '100vw'
  },

  imageWithLink: {
    width: 426,
    height: 277,
    isFluid: true,
    theme: ['mosaic', 'rounded'] as ImageTheme[],
    sources: [
      { media: '(min-width: 1600px)', width: 426, height: 277 },
      { media: '(min-width: 1280px)', width: 373, height: 242 },
      { media: '(min-width: 1024px)', width: 288, height: 187 },
      { media: '(min-width: 768px)', width: 336, height: 218 },
      { media: '(min-width: 510px)', width: 462, height: 300 },
      { media: '(max-width: 509px)', width: 282, height: 183 }
    ]
  },

  proposalShortcuts: {
    width: 432,
    height: 240,
    isFluid: true,
    theme: ['mosaic', 'rounded'] as ImageTheme[],
    sources: [
      { media: '(min-width: 1600px)', width: 432, height: 240 },
      { media: '(min-width: 1280px)', width: 379, height: 210 },
      { media: '(min-width: 1024px)', width: 452, height: 251 },
      { media: '(min-width: 768px)', width: 340, height: 189 },
      { media: '(min-width: 510px)', width: 462, height: 257 },
      { media: '(max-width: 509px)', width: 330, height: 183 }
    ],
    sizes:
      '(min-width: 1600px) 432px, (min-width: 1280px) 379px, (min-width: 1024px) 452px, (min-width: 768px) 340px, (min-width: 510px) 462px, 330px'
  },

  satelliteHeader: {
    isFluid: true,
    sizes: '100vw'
  },

  mediaBlock: {
    isFluid: true,
    theme: 'anchored' as ImageTheme,
    sizes: '100vw'
  },

  educationBlock: {
    isFluid: true,
    theme: 'anchored' as ImageTheme
  },

  largeArticlePage: {
    isFluid: true,
    theme: 'anchored' as ImageTheme
  },

  eventImage: {
    card: {
      width: 250,
      height: 250,
      isFluid: true,
      theme: 'roundedSmall' as ImageTheme,
      sizes: '250px'
    },

    article: {
      width: 660,
      height: 700,
      isFluid: true,
      theme: 'roundedSmall' as ImageTheme,
      sources: [
        { media: '(min-width: 1600px)', width: 660, height: 700 },
        { media: '(min-width: 1280px)', width: 576, height: 700 },
        { media: '(min-width: 1024px)', width: 461, height: 700 },
        { media: '(min-width: 768px)', width: 346, height: 700 },
        { media: '(min-width: 510px)', width: 416, height: 400 },
        { media: '(max-width: 509px)', width: 254, height: 400 }
      ],
      sizes:
        '(min-width: 1600px) 660px, (min-width: 1280px) 576px, (min-width: 1024px) 461px, (min-width: 768px) 346px, (min-width: 510px) 416px, 254px'
    }
  },

  imageWithCaption: {
    width: 888,
    height: 500,
    isFluid: true,
    sizes: '(min-width: 888px) 888px, 100vw',
    external: {
      width: 880,
      height: 550
    }
  },

  timelineEvent: {
    width: 360,
    height: 240,
    isFluid: true,
    sizes: '(min-width: 1320px) 360px, (min-width: 768px) 720px, 100vw'
  },

  statsLink: {
    width: 448,
    height: 300,
    isFluid: true,
    sizes: '(min-width: 768px) 33vw, 70vw'
  },

  statsLinkListAbout: {
    width: 1344,
    height: 756,
    isFluid: true,
    sizes: '100vw'
  },

  articleBlockDocument: {
    width: 136,
    height: 193,
    isFluid: true,
    theme: 'anchored' as ImageTheme
  },

  portfolioHeader: {
    width: 230,
    height: 230,
    responsive: false
  },

  listItemImage: {
    width: 150,
    height: 113,
    isFluid: true,
    sources: [
      { media: '(min-width: 768px)', width: 150, height: 113 },
      { media: '(max-width: 767px)', width: 135, height: 101 }
    ],
    sizes: '(min-width: 768px) 150px, 135px'
  },

  listItemImageSmall: {
    width: 120,
    height: 90,
    isFluid: true,
    sizes: '120px'
  },

  icon: {
    small: {
      width: 24,
      height: 24,
      responsive: false
    },
    medium: {
      width: 32,
      height: 32,
      responsive: false
    },
    large: {
      width: 40,
      height: 40,
      responsive: false
    }
  }
};

export default imageSizes;
